if (!customElements.get('swiper-component')) {
    class SwiperComponent extends HTMLElement {
      constructor() {
        super();
        this.initialized = false;
      }
  
      connectedCallback() {
        if (this.initialized) return;
        if (!this.classList.contains('swiper')) return;
        if (!this.querySelector('.swiper-wrapper')) return;
  
        this.initialized = true;
  
        this.parseConfig();
        this.waitForSwiperAndInit();
      }
  
      parseConfig() {
        const ds = this.dataset;
  
        this.desktopPerPage = parseFloat(ds.desktopperpage) || 4;
        this.tabletPerPage = parseFloat(ds.tabletperpage) || 3;
        this.mobilePerPage = parseFloat(ds.mobileperpage) || 1;
  
        this.desktopPerMove = parseInt(ds.desktoppermove) || 1;
        this.tabletPerMove = parseInt(ds.tabletpermove) || 1;
        this.mobilePerMove = parseInt(ds.mobilepermove) || 1;
  
        this.desktopGap = parseInt(ds.desktopslidegap) || 48;
        this.tabletGap = parseInt(ds.tabletslidegap) || 32;
        this.mobileGap = parseInt(ds.mobileslidegap) || 24;
  
        this.type = ds.type || 'loop';
        this.speed = parseInt(ds.speed) || 1500;
        this.autoplay = ds.autoplay === 'true';
        this.autoplaySpeed = parseInt(ds.autoplaySpeed) || 3000;
        this.disableDrag = ds.disabledrag === 'true';
  
        this.showArrows = ds.showarrows === 'true';
        this.showArrowsOnMobile = ds.showarrowsonmobile === 'true';
        this.showDots = ds.showdots === 'true';
        this.showDotsOnMobile = ds.showdotsonmobile === 'true';
  
        this.syncSelector = ds.carouselsyncselector || null;
      }
  
      waitForSwiperAndInit() {
        if (typeof window.Swiper !== 'undefined') {
          this.initSwiper();
          return;
        }
        // Swiper global is loaded via a deferred <script> in the same order as
        // this file, so it is normally available by now. This DOMContentLoaded
        // fallback handles edge cases like the theme editor re-executing scripts.
        document.addEventListener(
          'DOMContentLoaded',
          () => {
            if (typeof window.Swiper !== 'undefined') this.initSwiper();
          },
          { once: true }
        );
      }
  
      buildOptions() {
        const options = {
          slidesPerView: this.mobilePerPage,
          slidesPerGroup: this.mobilePerMove,
          spaceBetween: this.mobileGap,
          loop: this.type === 'loop',
          speed: this.speed,
          allowTouchMove: !this.disableDrag,
          watchOverflow: true,
          breakpoints: {
            768: {
              slidesPerView: this.tabletPerPage,
              slidesPerGroup: this.tabletPerMove,
              spaceBetween: this.tabletGap,
            },
            1024: {
              slidesPerView: this.desktopPerPage,
              slidesPerGroup: this.desktopPerMove,
              spaceBetween: this.desktopGap,
            },
          },
        };
  
        if (this.autoplay) {
          options.autoplay = {
            delay: this.autoplaySpeed,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          };
        }
  
        const nextEl = this.findControl('.swiper-button-next');
        const prevEl = this.findControl('.swiper-button-prev');
        if ((this.showArrows || this.showArrowsOnMobile) && nextEl && prevEl) {
          options.navigation = { nextEl, prevEl };
          if (!this.showArrowsOnMobile) nextEl.classList.add('hide-on-mobile');
          if (!this.showArrowsOnMobile) prevEl.classList.add('hide-on-mobile');
          if (!this.showArrows) {
            nextEl.classList.add('hide-on-desktop');
            prevEl.classList.add('hide-on-desktop');
          }
        }
  
        const paginationEl = this.findControl('.swiper-pagination');
        if ((this.showDots || this.showDotsOnMobile) && paginationEl) {
          options.pagination = { el: paginationEl, clickable: true };
          if (!this.showDotsOnMobile) paginationEl.classList.add('hide-on-mobile');
          if (!this.showDots) paginationEl.classList.add('hide-on-desktop');
        }
  
        return options;
      }
  
      findControl(selector) {
        const inside = this.querySelector(selector);
        if (inside) return inside;
        if (this.parentElement) {
          return this.parentElement.querySelector(':scope > ' + selector) || null;
        }
        return null;
      }
  
      initSwiper() {
        if (this.syncSelector) {
          this.initSwiperSync();
        } else {
          this.swiper = new Swiper(this, this.buildOptions());
        }
      }
  
      initSwiperSync() {
        const thumbsEl = document.querySelector(this.syncSelector);
        if (!thumbsEl) {
          console.warn(
            '[swiper-component] carouselsyncselector "' +
              this.syncSelector +
              '" did not match any element. Falling back to a standalone carousel.'
          );
          this.swiper = new Swiper(this, this.buildOptions());
          return;
        }
  
        // The thumbs element must be a plain .swiper container, NOT another
        // <swiper-component>, because nested SwiperComponents each run their own
        // new Swiper() in connectedCallback, which would collide with the thumbs
        // Swiper instance we create below.
        if (thumbsEl.tagName && thumbsEl.tagName.toLowerCase() === 'swiper-component') {
          console.warn(
            '[swiper-component] thumbs element "' +
              this.syncSelector +
              '" is itself a <swiper-component>; use a plain <div class="swiper"> ' +
              'as the thumbs container to avoid double-initialization.'
          );
        }
  
        const ds = thumbsEl.dataset;
        const thumbsOptions = {
          slidesPerView: parseFloat(ds.mobileperpage) || parseFloat(ds.desktopperpage) || 4,
          spaceBetween: parseInt(ds.mobileslidegap) || parseInt(ds.desktopslidegap) || 0,
          watchSlidesProgress: true,
          allowTouchMove: !(ds.disabledrag === 'true'),
          loop: (ds.type || 'slide') === 'loop',
          breakpoints: {
            768: {
              slidesPerView: parseFloat(ds.tabletperpage) || parseFloat(ds.desktopperpage) || 4,
              spaceBetween: parseInt(ds.tabletslidegap) || parseInt(ds.desktopslidegap) || 0,
            },
            1024: {
              slidesPerView: parseFloat(ds.desktopperpage) || 4,
              spaceBetween: parseInt(ds.desktopslidegap) || 0,
            },
          },
        };
  
        this.thumbsSwiper = new Swiper(thumbsEl, thumbsOptions);
  
        const mainOptions = this.buildOptions();
        mainOptions.thumbs = { swiper: this.thumbsSwiper };
        this.swiper = new Swiper(this, mainOptions);
      }
  
      refresh() {
        if (this.swiper) this.swiper.update();
        if (this.thumbsSwiper) this.thumbsSwiper.update();
      }
  
      disconnectedCallback() {
        if (this.swiper && typeof this.swiper.destroy === 'function') {
          this.swiper.destroy(true, true);
        }
        if (this.thumbsSwiper && typeof this.thumbsSwiper.destroy === 'function') {
          this.thumbsSwiper.destroy(true, true);
        }
      }
    }
  
    window.SwiperComponent = SwiperComponent;
    customElements.define('swiper-component', SwiperComponent);
  
    // Shopify theme editor integration. `shopify:section:load` fires after a
    // section's HTML is re-inserted; the existing connectedCallback will run
    // on the new element automatically, so we only need to refresh siblings
    // whose layout may shift. `shopify:section:select` / `shopify:block:select`
    // can fire while the slide is hidden (e.g. inside a tab), so we call
    // refresh() to recompute Swiper's dimensions once it becomes visible.
    document.addEventListener('shopify:section:load', (e) => {
      const root = e.target;
      if (!root || typeof root.querySelectorAll !== 'function') return;
      root.querySelectorAll('swiper-component').forEach((el) => {
        if (typeof el.refresh === 'function') el.refresh();
      });
    });
  
    const refreshWithin = (e) => {
      const root = e.target;
      if (!root || typeof root.querySelectorAll !== 'function') return;
      const host = root.closest ? root.closest('swiper-component') : null;
      if (host && typeof host.refresh === 'function') host.refresh();
      root.querySelectorAll('swiper-component').forEach((el) => {
        if (typeof el.refresh === 'function') el.refresh();
      });
    };
    document.addEventListener('shopify:section:select', refreshWithin);
    document.addEventListener('shopify:block:select', refreshWithin);
  }
  